import { hashAvatarSeed } from '@cognitoforms/utils/avatar';
import type { Entity } from '@cognitoforms/model.js';

/**
 * Gets a field value from an entity by navigating a dot-separated field path.
 * Uses `unknown` type to enforce proper type narrowing at call sites.
 */
export function getFieldValue(entity: Entity, fieldPath: string): unknown {
	const parts = fieldPath.split('.');
	let value: unknown = entity;
	for (const part of parts) {
		value = (value as Record<string, unknown>)?.[part];
	}
	return value;
}

/**
 * Extracts initials from first and last name, filtering special characters.
 * Matches server-side logic in IndexBuilder.GetImageFallback.
 */
export function extractInitials(first: string | undefined, last: string | undefined): string {
	let initials = '';

	if (first) {
		const c = first[0];
		// Filter pipe character (used as delimiter in image value)
		if (c !== '|')
			initials += c;
	}

	if (last) {
		const c = last[0];
		// Filter dash (would create "F-" prefix) and pipe characters
		if (c !== '-' && c !== '|')
			initials += c;
	}

	return initials.toUpperCase();
}

interface NameValue {
	First?: string;
	Last?: string;
}

function isNameValue(value: unknown): value is NameValue {
	return value !== null && typeof value === 'object' && ('First' in value || 'Last' in value);
}

interface FileDataRef {
	Id: string;
}

/**
 * File IDs are guaranteed to start with "F-" prefix.
 * Server generates via FileData.GenerateId() which hardcodes this format.
 */
export function isFileId(value: unknown): value is string {
	return typeof value === 'string' && value.startsWith('F-');
}

/**
 * Extracts a FileDataRef from a value that may be a direct object or an array.
 * Only returns a FileDataRef if the Id is a valid file ID (starts with F-).
 */
function getFileDataRef(value: unknown): FileDataRef | null {
	// Handle array case - FileDataRef fields return as single-element arrays
	if (Array.isArray(value) && value.length > 0) {
		value = value[0];
	}
	if (value !== null &&
		typeof value === 'object' &&
		'Id' in value &&
		isFileId((value as FileDataRef).Id)) {
		return value as FileDataRef;
	}
	return null;
}

/**
 * Computes the image value for a lookup entry, matching server-side logic.
 * Returns either a file reference (F-{fileId}) or initials with hash ({initials}|{hash}).
 */
export function computeImageValue(
	entry: Entity,
	imageFieldPath: string | undefined,
	nameFieldPath: string | undefined,
	emailFieldPath: string | undefined
): string | undefined {
	if (!imageFieldPath)
		return undefined;

	// Check if raw field value is a FileDataRef (file upload field)
	const rawImageValue = getFieldValue(entry, imageFieldPath);
	const fileDataRef = getFileDataRef(rawImageValue);
	if (fileDataRef) {
		return fileDataRef.Id;
	}

	// Try to get the image field value via toString (handles existing F- format from index)
	const imageValue = entry.toString(`[${imageFieldPath}]`);
	if (imageValue?.startsWith('F-'))
		return imageValue;

	// For person lookups, compute initials + email hash
	if (nameFieldPath) {
		const nameValue = getFieldValue(entry, nameFieldPath);
		if (isNameValue(nameValue)) {
			const initials = extractInitials(nameValue.First, nameValue.Last);
			const email = emailFieldPath ? entry.toString(`[${emailFieldPath}]`) : '';
			const seed = hashAvatarSeed(email || '');
			return `${initials}|${seed}`;
		}
	}

	return undefined;
}
